import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0TagBalance } from '@factorialco/f0-react'
import { BankingBankAccountOverviewFragment } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'

import { bankAccountLastDigits, bankLogo } from 'modules/banking/utils'

interface BalancePercentageTagProps {
  bankAccount: BankingBankAccountOverviewFragment
  amountFormatter: (value: number, currency: string) => string
}

const calculateBalancePercentageChange = (currentBalance: number, previousBalance: number) => {
  return ((currentBalance - previousBalance) * 100) / Math.abs(previousBalance)
}

const BalancePercentageTag = ({
  percentChange,
  amount,
  currency,
  locale,
}: {
  percentChange: number
  amount: number
  currency: string
  locale: string
}) => {
  const percentage =
    percentChange !== Infinity && !isNaN(percentChange)
      ? {
          numericValue: percentChange,
          formatterOptions: {
            decimalPlaces: 2,
            locale: locale,
          },
        }
      : null

  return (
    <F0TagBalance
      percentage={percentage}
      amount={{
        numericValue: {
          value_x100: amount,
          units: currency,
        },
        formatterOptions: {
          decimalPlaces: 2,
          locale: locale,
        },
      }}
      nullText="-"
    />
  )
}

export const BankAccountItem = ({ bankAccount, amountFormatter }: BalancePercentageTagProps) => {
  const { account } = bankAccount
  return (
    <Box flexDirection="row" alignItems="center" justifyContent="spaceBetween">
      <Box flexDirection="row" gap="s8" alignItems="center">
        {bankLogo(account, bankAccount.accountAlias || account.institution?.name || '')}
        <Box>
          <Text.Normal size="200" weight="medium">
            {bankAccount.accountAlias || account.institution?.name || ''}
          </Text.Normal>
          <Text.Normal size="200" weight="medium">
            {bankAccountLastDigits(bankAccount)}
          </Text.Normal>
        </Box>
      </Box>
      <Box gap="s2" alignItems="flexEnd" textAlign="right">
        <Text.Normal
          size="200"
          weight="medium"
          color={bankAccount.accountBalanceCents < 0 ? 'radicalRed1200' : 'grey800'}
        >
          {amountFormatter(bankAccount.accountBalanceCents, bankAccount.currency.toUpperCase())}
        </Text.Normal>

        <BalancePercentageTag
          percentChange={calculateBalancePercentageChange(
            bankAccount.accountBalanceCents,
            bankAccount.transactionsConnection.nodes[0]?.balanceAfterCents || 0
          )}
          amount={
            bankAccount.accountBalanceCents -
            (bankAccount.transactionsConnection.nodes[0]?.balanceAfterCents || 0)
          }
          locale={useCurrentSession().access.locale}
          currency={bankAccount.currency.toUpperCase()}
        />
      </Box>
    </Box>
  )
}
